import { useEffect, useState } from 'react';

import { Box, Button, Chip, Container, Typography } from '@mui/material';
import Grid from '@mui/material/Grid';
import { AnimatePresence, motion } from 'framer-motion';
import { Link } from 'react-router';

import { getOr } from '@/api/client';
import { RUTAS } from '@/api/rutas';
import { BuscadorDestacado } from '@/components/BuscadorDestacado';
import { MedirVisita } from '@/components/MedirVisita';
import { SIN_RUBRO, slugDeRubro } from '@/lib/rubros';
import type { Imagen } from '@/tipos';

import type { Route } from './+types/inicio';

const CADA_CUANTO_ROTA = 5000;

/**
 * `filtro` es el valor que entiende el catálogo, no el nombre que se muestra. SP no lo tiene
 * porque no es una marca de la columna MARCA de la planilla: su logo va igual, sin enlace, en
 * vez de mandar a un catálogo filtrado que devolvería cero productos.
 */
/** Cuántos rubros se ofrecen en la portada. Los de más piezas, que son los más buscados. */
const RUBROS_EN_LA_PORTADA = 12;

const MARCAS: { nombre: string; logo: string; filtro?: string }[] = [
  { nombre: 'HADA', logo: '/img/marcas/hada-logo.webp', filtro: 'HADA' },
  { nombre: 'Hafeen', logo: '/img/marcas/hafeen-logo.webp', filtro: 'HAFEEN' },
  { nombre: 'SP', logo: '/img/marcas/sp-logo.webp' },
  { nombre: 'VTC', logo: '/img/marcas/vtc-logo.webp', filtro: 'VTC' },
];

/**
 * El fondo de la portada es una imagen de CSS, y a esas el navegador las descubre recién cuando
 * terminó de armar el CSS: para entonces ya perdió el arranque. El `preload` la pide junto con
 * el HTML, que es lo que mide el LCP.
 *
 * Y se pide la que corresponde al ancho de la pantalla. Era un JPG de 315 KB para todos: un
 * celular bajaba una foto de 1600 píxeles para mostrarla en 400. En WebP y por tamaños son 29 KB
 * en el celular y 79 KB en una pantalla grande.
 */
export function links(): Route.LinkDescriptors {
  // Sin `fetchPriority`: el tipo `LinkDescriptor` de React Router no lo tiene, y un
  // `preload` de imagen ya sale con prioridad alta. Forzar el tipo por un atributo que
  // el navegador deduce solo no vale la pena.
  return [
    { rel: 'preload', as: 'image', href: '/hero/hero-800.webp', media: '(max-width: 599px)' },
    {
      rel: 'preload',
      as: 'image',
      href: '/hero/hero-1280.webp',
      media: '(min-width: 600px) and (max-width: 1279px)',
    },
    { rel: 'preload', as: 'image', href: '/hero/hero-1600.webp', media: '(min-width: 1280px)' },
  ];
}

export function meta(): Route.MetaDescriptors {
  return [
    { title: 'CKING | Repuestos para Motos en Argentina — Importadora desde 2009' },
    {
      name: 'description',
      content:
        'CKING importa y distribuye repuestos de alta calidad para motocicletas en Argentina. ' +
        'Marcas HADA, HAFEEN, VTC, ARTICULOS PARA BENELLI. Catalogo digital disponible.',
    },
    { tagName: 'link', rel: 'canonical', href: 'https://cking.com.ar/' },
    // La imagen que se ve al pegar el link del sitio. Va por pantalla y no en la plantilla de
    // todas: si estuviera en las dos, la ficha de un producto mostraría la portada del sitio en
    // vez de la foto de la pieza.
    // En JPG y no en WebP: es la que leen los robots de WhatsApp y Facebook para armar la
    // vista previa, y no todos entienden WebP. La pide el robot, no la persona.
    { property: 'og:image', content: 'https://cking.com.ar/hero/hero.jpg' },
  ];
}

export async function loader({ request }: Route.LoaderArgs) {
  const { images } = await getOr<{ images: Imagen[] }>(
    RUTAS.imagenes.coleccionPublica('hero'),
    { images: [] },
    { request },
  );

  // Los rubros más grandes, para la sección de abajo. Es una consulta más y es la que pone en
  // la portada los primeros links hacia las 97 páginas de rubro: sin un link desde algún lado,
  // una página existe y no la encuentra nadie.
  const { categories } = await getOr<{ categories: TarjetaDeRubroApi[] }>(
    RUTAS.catalogo.categoriasEnTarjetas,
    { categories: [] },
    { request },
  );

  const rubros = categories
    .filter(rubro => rubro.name.trim().toUpperCase() !== SIN_RUBRO)
    .sort((a, b) => b.products - a.products)
    .slice(0, RUBROS_EN_LA_PORTADA)
    .map(rubro => ({ nombre: rubro.name, total: rubro.products, slug: slugDeRubro(rubro.name) }));

  // Los banners salen ya en el HTML. Antes se pedían desde el navegador y la portada arrancaba
  // con un "Cargando…" a pantalla completa: el primer segundo de cada visita era una pantalla
  // gris.
  return { banners: images.sort((a, b) => a.display_order - b.display_order), rubros };
}

export default function Inicio({ loaderData }: Route.ComponentProps) {
  return (
    <>
      <MedirVisita path="/" />

      <motion.section
        aria-label="Presentacion"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
        <Portada banners={loaderData.banners} />
      </motion.section>

      <motion.section
        aria-label="Buscar una pieza"
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6 }}
      >
        <BuscadorDestacado />
      </motion.section>

      <motion.section
        aria-label="Marcas"
        initial={{ opacity: 0, y: 50 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.7, delay: 0.2 }}
      >
        <CarruselDeMarcas />
      </motion.section>

      <motion.section
        aria-label="Sobre nosotros"
        initial={{ opacity: 0, y: 50 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.7, delay: 0.2 }}
      >
        <SobreNosotros />
      </motion.section>

      <motion.section
        aria-label="Rubros"
        initial={{ opacity: 0, y: 50 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.7, delay: 0.2 }}
      >
        <RubrosDestacados rubros={loaderData.rubros} />
      </motion.section>
    </>
  );
}

function Portada({ banners }: { banners: Imagen[] }) {
  const [actual, setActual] = useState(0);

  useEffect(() => {
    if (banners.length < 2) return;

    const reloj = setInterval(() => {
      setActual(anterior => (anterior + 1) % banners.length);
    }, CADA_CUANTO_ROTA);

    return () => clearInterval(reloj);
  }, [banners.length]);

  const banner = banners[actual];

  return (
    <Box
      sx={{
        position: 'relative',
        mt: '-80px',
        width: '100%',
        height: { xs: 'calc(90vh + 80px)', md: 'calc(100vh + 80px)' },
        display: 'flex',
        alignItems: 'center',
        overflow: 'hidden',
      }}
    >
      {/* El fondo se escala 110% para que no se vean bordes al recortar. */}
      <Box
        sx={{
          position: 'absolute',
          top: '-5%',
          left: '-5%',
          width: '110%',
          height: '110%',
          backgroundImage: 'url(/hero/hero-800.webp)',
          // La misma foto en tres tamaños: el navegador baja la que le sirve y no más.
          '@media (min-width: 600px)': { backgroundImage: 'url(/hero/hero-1280.webp)' },
          '@media (min-width: 1280px)': { backgroundImage: 'url(/hero/hero-1600.webp)' },
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          filter: 'brightness(0.5)',
          zIndex: 0,
        }}
      />

      {banner && (
        <Box
          sx={{
            position: 'absolute',
            top: { xs: 80, md: 85 },
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 3,
            width: { xs: '92%', md: '80%' },
            maxWidth: '1000px',
            aspectRatio: '5 / 1',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            overflow: 'hidden',
          }}
        >
          <AnimatePresence mode="wait">
            <motion.div
              key={banner.id}
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -50 }}
              transition={{ duration: 0.5 }}
              style={{
                position: 'absolute',
                width: '100%',
                height: '100%',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <Link
                to={`/catalogo?search=${encodeURIComponent(banner.name)}`}
                aria-label={`Buscar ${banner.name}`}
              >
                <motion.img
                  src={banner.url}
                  alt={banner.alt_text ?? banner.name}
                  draggable={false}
                  // Casi siempre es el elemento más grande de la primera pantalla, o sea el que
                  // define el LCP. Por defecto una imagen sale en la cola de prioridad baja.
                  fetchPriority="high"
                  decoding="async"
                  style={{
                    maxWidth: '100%',
                    maxHeight: '100%',
                    width: 'auto',
                    height: 'auto',
                    objectFit: 'contain',
                    cursor: 'pointer',
                    borderRadius: '8px',
                    boxShadow: '0 4px 10px rgba(0,0,0,0.4)',
                  }}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                />
              </Link>
            </motion.div>
          </AnimatePresence>
        </Box>
      )}

      <Container
        maxWidth="lg"
        sx={{ pt: banner ? { xs: '160px', md: '180px' } : { xs: '100px', md: '120px' } }}
      >
        <Box
          sx={{ maxWidth: { xs: '100%', md: '70%' }, zIndex: 1, mt: banner ? { xs: 2, md: 4 } : 0 }}
        >
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 0.99, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Typography
              component="h1"
              variant="h2"
              sx={{
                fontWeight: 800,
                fontSize: { xs: '2rem', md: '3.5rem' },
                mb: 2,
                textTransform: 'uppercase',
                textShadow: '2px 2px 4px rgba(0,0,0,0.5)',
                lineHeight: 1.2,
              }}
            >
              {/* "Motopartes" no lo busca nadie: quien necesita una pieza escribe "repuestos
                  para motos", y el encabezado principal es lo que más pesa dentro de la página. */}
              IMPORTADORA DE{' '}
              <Box component="span" sx={{ color: 'primary.main' }}>
                REPUESTOS PARA MOTOS
              </Box>
            </Typography>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 0.99, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <Typography
              variant="h6"
              component="p"
              sx={{
                mb: 4,
                maxWidth: '95%',
                fontWeight: 400,
                color: 'rgba(255, 255, 255, 0.9)',
                textShadow: '1px 1px 2px rgba(0,0,0,0.5)',
                lineHeight: 1.6,
              }}
            >
              CKING es una empresa argentina que desde 2009 está dedicada a la importación y la
              comercialización de repuestos para motocicletas de primera calidad...
            </Typography>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 0.99, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Button
                component={Link}
                to="/catalogo"
                variant="contained"
                color="primary"
                size="large"
                sx={{ fontWeight: 'bold', px: 4, py: 1.5, fontSize: '1rem' }}
              >
                Ver Catálogo
              </Button>

              <Button
                component={Link}
                to="/contactenos"
                variant="outlined"
                size="large"
                sx={{
                  borderColor: 'white',
                  color: 'white',
                  fontWeight: 'bold',
                  px: 4,
                  py: 1.5,
                  fontSize: '1rem',
                  '&:hover': {
                    borderColor: 'primary.main',
                    backgroundColor: 'rgba(255, 61, 0, 0.1)',
                  },
                }}
              >
                Contáctanos
              </Button>
            </Box>
          </motion.div>
        </Box>
      </Container>
    </Box>
  );
}

/** El subrayado corto debajo de cada título de sección. Se repite en todo el sitio. */
const SUBRAYADO = {
  content: '""',
  position: 'absolute',
  width: '60px',
  height: '3px',
  bottom: '-10px',
  left: '50%',
  backgroundColor: 'primary.main',
  transform: 'translateX(-50%)',
} as const;

function CarruselDeMarcas() {
  return (
    <Box sx={{ py: 10, bgcolor: '#0a0a0a' }}>
      <Container maxWidth="lg">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <Typography
            component="h2"
            variant="h3"
            align="center"
            sx={{
              mb: 6,
              fontWeight: 700,
              position: 'relative',
              display: 'inline-block',
              left: '50%',
              transform: 'translateX(-50%)',
              '&::after': SUBRAYADO,
            }}
          >
            Nuestras Marcas
          </Typography>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, delay: 0.2 }}
        >
          <CintaDeMarcas />
        </motion.div>
      </Container>
    </Box>
  );
}

/**
 * Las marcas, en cinta continua.
 *
 * Era un carrusel de swiper con flechas y teclado para cuatro logos que entran todos juntos en
 * cualquier pantalla de escritorio: el carrusel no llevaba a ninguna parte porque no había nada
 * más para mostrar. Una cinta que corre sola dice lo mismo -son estas marcas- sin pedir que
 * nadie la opere, y ahora cada logo lleva al catálogo filtrado por esa marca, que es lo que
 * alguien que reconoce el logo quiere hacer.
 *
 * El truco de la cinta: el mismo grupo dos veces y un desplazamiento del 50%. Cuando la
 * animación vuelve a cero, la segunda copia está exactamente donde estaba la primera y no se ve
 * el salto. El grupo repite las marcas varias veces para que llene una pantalla ancha.
 */
function CintaDeMarcas() {
  const grupo = [...MARCAS, ...MARCAS, ...MARCAS, ...MARCAS];

  return (
    <Box
      sx={{
        mt: 4,
        overflow: 'hidden',
        // Los bordes se desvanecen: sin esto la cinta aparece y desaparece de golpe contra el
        // borde del contenedor y se lee como un recorte, no como algo que sigue de largo.
        maskImage: 'linear-gradient(to right, transparent, #000 8%, #000 92%, transparent)',
        '&:hover .cinta': { animationPlayState: 'paused' },
      }}
    >
      <Box
        className="cinta"
        sx={{
          display: 'flex',
          width: 'max-content',
          '@keyframes correr': {
            from: { transform: 'translateX(0)' },
            to: { transform: 'translateX(-50%)' },
          },
          animation: 'correr 45s linear infinite',
          '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
        }}
      >
        {[0, 1].map(copia => (
          <Box
            key={copia}
            // La segunda copia existe sólo para que el bucle no tenga costura: para un lector de
            // pantalla serían las mismas marcas dichas dos veces.
            aria-hidden={copia === 1}
            sx={{ display: 'flex', flexShrink: 0 }}
          >
            {grupo.map((marca, indice) => (
              <LogoDeMarca key={`${copia}-${indice}`} marca={marca} sinFoco={copia === 1} />
            ))}
          </Box>
        ))}
      </Box>
    </Box>
  );
}

function LogoDeMarca({ marca, sinFoco }: { marca: (typeof MARCAS)[number]; sinFoco: boolean }) {
  const logo = (
    <Box
      component="img"
      src={marca.logo}
      alt={sinFoco ? '' : `Logo de ${marca.nombre}`}
      loading="lazy"
      sx={{
        maxWidth: '100%',
        maxHeight: '100%',
        objectFit: 'contain',
        // Apagados hasta que se los mira: cuatro logos a todo color compitiendo entre sí hacen
        // ruido, y el que interesa es el que tiene el mouse encima.
        opacity: 0.55,
        transition: 'opacity .3s ease, transform .3s ease',
      }}
    />
  );

  const caja = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: 120,
    width: 200,
    flexShrink: 0,
    px: 3,
    '&:hover img, &:focus-visible img': { opacity: 1, transform: 'scale(1.06)' },
  } as const;

  if (!marca.filtro || sinFoco) {
    return <Box sx={caja}>{logo}</Box>;
  }

  return (
    <Box
      component={Link}
      to={`/catalogo?brand=${encodeURIComponent(marca.filtro)}`}
      aria-label={`Ver el catálogo de ${marca.nombre}`}
      sx={caja}
    >
      {logo}
    </Box>
  );
}

function SobreNosotros() {
  return (
    <Box sx={{ py: 12, bgcolor: '#121212' }}>
      <Container maxWidth="lg">
        <Grid container spacing={6} sx={{ alignItems: 'center' }}>
          <Grid>
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <Typography
                component="h2"
                variant="h3"
                sx={{
                  mb: 3,
                  fontWeight: 700,
                  position: 'relative',
                  display: 'inline-block',
                  '&::after': { ...SUBRAYADO, left: 0, transform: 'none' },
                }}
              >
                Más de 15 años de experiencia
              </Typography>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <Typography
                variant="body1"
                sx={{ mb: 4, fontSize: '1.1rem', color: 'rgba(255, 255, 255, 0.8)' }}
              >
                Somos una empresa importadora y distribuidora de repuestos y accesorios para
                motocicletas con más de 15 años de experiencia en el mercado. Trabajamos con las
                mejores marcas a nivel mundial para ofrecer productos de alta calidad a precios
                competitivos.
              </Typography>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              <Button
                component={Link}
                to="/sobre-nosotros"
                variant="contained"
                color="primary"
                size="large"
                sx={{ fontWeight: 'bold', px: 4, py: 1.5, fontSize: '1rem' }}
              >
                Conoce Nuestra Historia
              </Button>
            </motion.div>
          </Grid>

          <Grid>
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <Box
                component="img"
                // Pesaba 4 MB: 6192 píxeles de ancho para mostrarse en 500. Ahora son 73 KB, y
                // el celular baja la de 800.
                src="/img/sobre-nosotros/sobre-nosotros-1600.webp"
                srcSet="/img/sobre-nosotros/sobre-nosotros-800.webp 800w, /img/sobre-nosotros/sobre-nosotros-1600.webp 1600w"
                sizes="(max-width: 900px) 92vw, 560px"
                alt="Depósito de CKING con repuestos para motos"
                loading="lazy"
                sx={{
                  width: '100%',
                  height: 'auto',
                  borderRadius: '4px',
                  boxShadow: '0 10px 30px rgba(0, 0, 0, 0.3)',
                }}
              />
            </motion.div>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}

/** Un rubro como lo devuelve la API. */
interface TarjetaDeRubroApi {
  name: string;
  products: number;
  image: string | null;
}

/**
 * Los rubros más grandes, en la portada.
 *
 * Acá estaba "Distribuidores — En Construcción": un cartel de obra en la página más visitada del
 * sitio. En su lugar van los rubros con más piezas, que es lo que la gente busca de verdad
 * —"arandelas para moto", "árbol de levas"— y de paso son los primeros links que llevan a las 97
 * páginas de rubro.
 */
function RubrosDestacados({
  rubros,
}: {
  rubros: { nombre: string; total: number; slug: string }[];
}) {
  if (rubros.length === 0) return null;

  return (
    <Box sx={{ py: 10, bgcolor: '#0a0a0a' }}>
      <Container maxWidth="lg">
        <Typography
          component="h2"
          variant="h3"
          align="center"
          sx={{
            mb: 2,
            fontWeight: 700,
            position: 'relative',
            display: 'inline-block',
            left: '50%',
            transform: 'translateX(-50%)',
            '&::after': SUBRAYADO,
          }}
        >
          Rubros de repuestos
        </Typography>

        <Typography
          align="center"
          sx={{ color: 'rgba(255,255,255,.75)', maxWidth: 760, mx: 'auto', mb: 5 }}
        >
          Importamos y distribuimos repuestos para motos al por mayor en toda Argentina. Estos son
          los rubros con más piezas del catálogo.
        </Typography>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, justifyContent: 'center' }}>
          {rubros.map(rubro => (
            <Chip
              key={rubro.slug}
              component={Link}
              to={`/rubros/${rubro.slug}`}
              clickable
              variant="outlined"
              label={`${rubro.nombre} (${rubro.total})`}
              sx={{ borderColor: 'rgba(255,255,255,.2)' }}
            />
          ))}
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <Button component={Link} to="/rubros" variant="outlined" color="primary" size="large">
            Ver los rubros
          </Button>
        </Box>
      </Container>
    </Box>
  );
}
